/**
 * F4 (harvest & farm gate) command payloads, as sent in `commands[].payload` of POST /sync/commands.
 * THIS FILE IS THE CONTRACT for the uc-core handlers (F4.1-i, F4.1-k, F4.6-g, F4.10-b, F4.11-c,
 * F4-B1.a-b, F4-B2.*, F4-B3.b-*).
 *
 * Wire names follow the spec's engineering.api "command …" bodies (F4.1, F4.2, F4.4, F4.6, F4.7,
 * F4.8, F4.10, F4.11, F4-B1.a, F4-B2.a, F4-B3.b) with these deliberate, uniform rules:
 *  - UNITS: every spec field in kg is sent as INTEGER GRAMS with the suffix `_g`
 *    (`gross_kg` → `gross_g`, `tare_kg` → `tare_g`, `test_weight_kg` → `test_weight_g` …); every
 *    rupee figure is sent as INTEGER PAISE with the suffix `_paise` (`amount_inr` → `amount_paise`).
 *    Moisture `value` is an exact decimal string with one decimal ("12.4").
 *  - The command id, `occurred_at`, `actor_id`, `session_id` and `capture_mode` are the envelope's
 *    (offline/protocol.ts); payloads repeat `session_id` only where the spec body lists it.
 *  - Ids of things the server will create (lots, bags, measurements, samples, disputes, slips) are
 *    UUIDv7s made on the phone, so a replayed command creates nothing twice.
 *
 * Importing this module registers every schema, so `enqueueCommand` refuses a malformed payload on
 * the phone before it reaches the outbox.
 */
import { z } from 'zod'
import { registerCommand } from '@/offline'
import { ULID_RE } from '@/lib/ids'

const id = z.string().min(1)
/** A delivery is keyed by a ULID (units.delivery_ulid is char(26)). */
const deliveryUlid = z.string().regex(ULID_RE, 'delivery_ulid must be a ULID')
const isoTs = z.string().min(1)
const grams = z.number().int()
const paise = z.number().int()
/** Moisture % with one decimal, exact ("12.4"). */
const pctString = z.string().regex(/^\d{1,2}\.\d$/, 'percent with one decimal')
/** Printed slip number: digits only, leading zeros kept ("0403"). */
const slipNo = z.string().regex(/^\d{1,8}$/, 'digits only')

export const STREAMS = ['MAIN', 'CONV'] as const
export type Stream = (typeof STREAMS)[number]
export const ANSWERS = ['YES', 'NO', 'DONT_KNOW'] as const
export type StorageAnswer = (typeof ANSWERS)[number]
export const TAG_ENTRY = ['SCAN', 'TYPED'] as const
export type TagEntry = (typeof TAG_ENTRY)[number]

/** Why the phone refused a scan (F4.6-c refused-scan log; the server raises TAG_UNKNOWN / TAG_WRONG_KIND). */
export const REFUSAL_REASONS = [
  'TAG_UNKNOWN', // not in any roll of this village / not issued to this operator
  'TAG_WRONG_KIND', // a seal scanned as a tag, or a tag as a seal
  'ROLL_NOT_CONFIRMED', // her roll, but not confirmed for this session yet
  'TAG_VOID', // voided earlier (torn, walk-away, roll remainder)
  'TAG_ALREADY_USED', // bound to another bag / sample already (this phone or roster)
  'TAG_DUPLICATE_SCAN', // already on another bag of this delivery
] as const
export type RefusalReason = (typeof REFUSAL_REASONS)[number]

export const TAG_VOID_REASONS = [
  'TORN',
  'MISPRINT',
  'OTHER',
  'WALK_AWAY',
  'WITHDRAWN_IN_DISPUTE',
] as const
export type TagVoidReason = (typeof TAG_VOID_REASONS)[number]

// ---------------------------------------------------------------------------------------------
// F4.1 open_session / confirm_roll
// ---------------------------------------------------------------------------------------------

export const openSessionPayload = z.object({
  /** CS-{YYMMDD}-{village}-{prefix}-{nn}. The session id is the envelope `session_id`. */
  code: z.string().regex(/^CS-\d{6}-[A-Z0-9]+-[A-Z0-9]+-\d{2,}$/),
  group_id: id,
  /** APP for a normal day; PAPER for a paper day typed in later (F4-B3). */
  capture_mode: z.enum(['APP', 'PAPER']),
  /** The operator who runs the day (for PAPER: the paper-day operator; the typist is the actor). */
  operator_id: id,
  opened_at: isoTs,
  gps: z.object({ lat: z.number(), lng: z.number(), accuracy_m: z.number().nullable() }).nullable(),
  /** Picked scale; null = 'Not registered' (SCALE_UNREGISTERED), or V1 (no scale is picked). */
  scale_equipment_id: id.nullable(),
  /** Phone's view of the stamp (null when unregistered). The server re-checks. */
  stamp_ok: z.boolean().nullable(),
  /** V1: no scale test, both null. */
  test_weight_g: grams.positive().nullable(),
  test_weight_read_g: grams.nonnegative().nullable(),
  tag_batch_id: id.nullable(),
  seal_batch_id: id.nullable(),
  roster_version: z.string().nullable(),
  /** Phone ahead (+) / behind (−) the server at last contact; flag only. */
  clock_skew_s: z.number().int().nullable(),
  tags_in_hand: z.number().int().nonnegative(),
  seals_in_hand: z.number().int().nonnegative(),
  /** PAPER only: the receipt book used that day (F4-B3.b). */
  receipt_book_id: id.nullable().default(null),
})
export type OpenSessionPayload = z.infer<typeof openSessionPayload>

export const confirmRollPayload = z.object({
  batch_id: id,
  kind: z.enum(['BAG', 'SEAL']),
  first_payload: z.string().min(1),
  last_payload: z.string().min(1),
  spare: z.boolean(),
  /** last − first + 1 (ROLL_CONFIRMED.payload.tags_in_roll). */
  tags_in_roll: z.number().int().positive(),
})
export type ConfirmRollPayload = z.infer<typeof confirmRollPayload>

// ---------------------------------------------------------------------------------------------
// F4.2 – F4.10 commit_delivery (and its parts)
// ---------------------------------------------------------------------------------------------

export const identificationSchema = z.object({
  method: z.enum(['SEARCH', 'ROSTER_NO']),
  /** V1: no phone check, only the photo (he is found by roster number or name), left out. */
  phone_match: z.enum(['MATCHED', 'MISMATCH', 'NO_PHONE']).optional(),
  photo_confirmed: z.literal(true),
  /** V1: he had no photo on record; the operator took one at the gate (becomes his photo). */
  new_photo_media_id: id.optional(),
  candidates_shown: z.number().int().nonnegative(),
  /** V1: no season light on the phone (the ceiling is a nightly check), left out. */
  season_light: z.enum(['GREEN', 'AMBER']).optional(),
})

export const storageAnswersSchema = z.object({
  stored_over_7_days: z.enum(ANSWERS),
  tablet_or_powder: z.enum(ANSWERS),
  answered_at: isoTs,
})

/** 'SELF', or the person who brought it (F4.2). */
export const deliveredBySchema = z.union([
  z.literal('SELF'),
  z.object({
    name: z.string().min(1),
    phone: z.string().regex(/^\d{10}$/),
    photo_media_id: id,
  }),
])

export const phoneFiguresSchema = z.object({
  gross_g: grams,
  tare_g: grams,
  net_g: grams,
  payable_g: grams,
  /** payable × rate, rounded half up once. The server recomputes and compares exactly. */
  amount_paise: paise,
})
export type PhoneFigures = z.infer<typeof phoneFiguresSchema>

export const lotSchema = z
  .object({
    id,
    /** HL-{YYYY}-{MMDD}-{prefix}-{NNNN}, minted on the phone, never reused. */
    code: z.string().regex(/^HL-\d{4}-\d{4}-[A-Z0-9]+-\d{4,}$/),
    stream: z.enum(STREAMS),
    /** V1: no typed count, left out — the server sets bag_count from the tagged bags. */
    bag_count: z.number().int().nonnegative().optional(),
    crop_id: id,
    crop_variety_id: id.nullable(),
    /** V1: no field is picked, both null — the server takes his registered field. */
    cultivation_id: id.nullable(),
    no_cultivation_reason: z.enum(['FIELD_NOT_ENROLLED']).nullable(),
    rate_card_id: id,
    phone_figures: phoneFiguresSchema,
  })
  .refine((l) => l.cultivation_id === null || l.no_cultivation_reason === null, {
    message: 'cultivation_id or no_cultivation_reason, not both',
  })
export type LotPayload = z.infer<typeof lotSchema>

export const weightSchema = z.object({
  /** Measurement id (UUIDv7). */
  id,
  /** Gross on the scale, bag and sack together. */
  gross_g: grams.positive(),
  taken_at: isoTs,
  /** Out of weight_min..weight_max and typed twice (F4.6). */
  confirmed_twice: z.boolean(),
  /** Fix last bag / dispute re-weigh: the measurement this one supersedes. */
  supersedes_id: id.nullable(),
  /** Live scale photo; required when reading_photos_required is present. */
  photo_media_id: id.nullable().optional(),
})

export const bagSchema = z.object({
  /** Container id (UUIDv7). */
  id,
  unit_id: id,
  qr_payload: z.string().min(1),
  entry: z.enum(TAG_ENTRY),
  sack_type_id: id,
  /** Phone's copy of the sack tare; the server copies its own from sack_types. */
  tare_g: grams.nonnegative(),
  /** A sack typed at the gate (TYPED_AT_GATE): its name; tare_g is then the weight used. */
  sack_name: z.string().min(1).max(60).nullable().optional(),
  weights: z.array(weightSchema).min(1),
  /** Red-tie bags: the red-line reason and the inside photo (F4.3 → BAG_MARKED_CONV). */
  conv_reason: z.string().nullable(),
  conv_photo_media_id: id.nullable(),
  tagged_at: isoTs,
  /** A tagged bag that went home in a dispute (F4-B2.c): kept, left out of every total. */
  withdrawn: z.boolean().default(false),
})
export type BagPayload = z.infer<typeof bagSchema>

export const voidedTagSchema = z.object({
  qr_payload: z.string().min(1),
  kind: z.enum(['BAG', 'SEAL']),
  void_reason: z.enum(TAG_VOID_REASONS),
  /** The bag it was on (voiding it voids that bag in the draft), or null. */
  container_id: id.nullable(),
  occurred_at: isoTs,
})
export type VoidedTag = z.infer<typeof voidedTagSchema>

export const refusedScanSchema = z.object({
  payload: z.string().min(1),
  reason: z.enum(REFUSAL_REASONS),
  occurred_at: isoTs,
})
export type RefusedScan = z.infer<typeof refusedScanSchema>

export const moistureReadingSchema = z.object({
  id,
  value: pctString,
  /** Good-bag number the draw came from (1-based), or null for a farmer-picked re-draw bag. */
  bag_no: z.number().int().positive().nullable(),
  taken_at: isoTs,
  /** F4-B2.b moisture re-draw: the farmer picked the bags. */
  chosen_by_farmer: z.boolean().default(false),
  /** Live meter photo; required when reading_photos_required is present. */
  photo_media_id: id.optional(),
})

export const moistureSchema = z.object({
  equipment_id: id.nullable(),
  readings: z.array(moistureReadingSchema),
  /** True when no reading was taken (No meter today, or the supervisor's groundnut skip). */
  skipped: z.boolean(),
  /** Supervisor user id who allowed a groundnut lot without a reading. */
  skip_allowed_by: id.nullable(),
})
export type MoisturePayload = z.infer<typeof moistureSchema>

export const sampleSchema = z.object({
  id,
  seal_payload: z.string().min(1),
  seal_entry: z.enum(TAG_ENTRY),
  /** V1: the sample is not weighed or photographed; both left out. */
  weight_g: grams.positive().optional(),
  collected_at: isoTs,
  photo_media_id: id.optional(),
  /** Supervisor user id who allowed a sample under the crop minimum. */
  underweight_allowed_by: id.nullable(),
})
export type SamplePayload = z.infer<typeof sampleSchema>

export const DISPUTE_OUTCOMES = [
  'ACCEPTED_AFTER_RECHECK',
  'BAG_WITHDRAWN',
  'COMMITTED_DISPUTED',
] as const
export type DisputeOutcome = (typeof DISPUTE_OUTCOMES)[number]

/** F4-B2: one dispute raised at the gate, every value kept. */
export const disputeSchema = z.object({
  id,
  about: z.enum(['WEIGHT', 'GRADE', 'MOISTURE']),
  reason_code: z.string().min(1),
  /** WEIGHT: the tagged bag. */
  container_id: id.nullable(),
  /** GRADE: the bag's number in today's delivery (not tagged yet). */
  bag_position: z.number().int().positive().nullable(),
  /** WEIGHT: the purchase weight disputed. */
  disputed_measurement_id: id.nullable(),
  raised_at: isoTs,
  /** WEIGHT: the re-weigh (also in bags[].weights with supersedes_id); MOISTURE: the re-draws. */
  recheck_measurement_ids: z.array(id),
  /** GRADE: the new photo against the red-line card. */
  photo_ids: z.array(id),
  /** WEIGHT 'scale is wrong': the kit's test weight read in front of him (context SCALE_TEST). */
  test_weight: z
    .object({ id, test_weight_g: grams.positive(), read_g: grams.nonnegative(), taken_at: isoTs })
    .nullable(),
  chosen_by_farmer: z.boolean().nullable(),
  outcome: z.enum(DISPUTE_OUTCOMES),
  /** Settled at the gate: RECHECK_VALUE_STANDS | ORIGINAL_STANDS; BAG_WITHDRAWN; null when left open. */
  resolution_code: z.enum(['RECHECK_VALUE_STANDS', 'ORIGINAL_STANDS', 'BAG_WITHDRAWN']).nullable(),
  closed_at: isoTs.nullable(),
})
export type DisputePayload = z.infer<typeof disputeSchema>

export const slipSchema = z.object({
  slip_no: slipNo,
  /** Thumb-marked carbon copy (APP) or the book page (PAPER). */
  photo_media_id: id,
  farmer_agreed_at: isoTs.nullable(),
  /** He said the account last 4 shown is his. */
  account_confirmed: z.boolean(),
  /** Closed while he still disputes: payout held. */
  disputed: z.boolean(),
})

/** PAPER back-entry (F4-B3.b): the receipt_slips row. */
export const receiptSlipSchema = z.object({
  id,
  book_id: id,
  slip_no: slipNo,
  photo_id: id,
})

export const commitDeliveryPayload = z
  .object({
    /** Present on new live-app deliveries; older queued and paper commands remain compatible. */
    reading_photos_required: z.literal(true).optional(),
    /** Where the purchase was closed (new live deliveries). */
    gps: z
      .object({
        lat: z.number().min(-90).max(90),
        lng: z.number().min(-180).max(180),
        accuracy_m: z.number().min(0).nullable(),
        captured_at: isoTs,
      })
      .optional(),
    /** The supervisor who let it close without a position. */
    gps_skip_allowed_by: id.optional(),
    delivery_ulid: deliveryUlid,
    session_id: id,
    farmer_id: id,
    crop_id: id,
    identified_at: isoTs,
    identification: identificationSchema,
    storage_answers: storageAnswersSchema,
    delivered_by: deliveredBySchema,
    /** Counted before any bag was opened (F4.3). V1: not counted, left out. */
    bags_brought: z.number().int().positive().optional(),
    /** Probe draw from every good bag is in the sample bag. V1: left out. */
    probe_draws_done: z.boolean().optional(),
    lots: z.array(lotSchema).min(1).max(2),
    bags: z.array(bagSchema),
    voided_tags: z.array(voidedTagSchema),
    refused_scans: z.array(refusedScanSchema),
    moisture: moistureSchema,
    sample: sampleSchema.nullable(),
    outcome: z.object({ chosen: z.literal('ACCEPT'), accepted_at: isoTs }),
    disputes: z.array(disputeSchema),
    slip: slipSchema,
    receipt_slip: receiptSlipSchema.nullable(),
    /** The phone's close time = committed_at (PAPER: the time on the slip). */
    closed_at: isoTs,
  })
  .superRefine((payload, ctx) => {
    if (!payload.reading_photos_required) return
    payload.bags.forEach((bag, bagIndex) =>
      bag.weights.forEach((weight, weightIndex) => {
        if (!weight.photo_media_id) {
          ctx.addIssue({
            code: 'custom',
            message: 'Every scale reading needs a photo',
            path: ['bags', bagIndex, 'weights', weightIndex, 'photo_media_id'],
          })
        }
      }),
    )
    payload.moisture.readings.forEach((reading, readingIndex) => {
      if (!reading.photo_media_id) {
        ctx.addIssue({
          code: 'custom',
          message: 'Every moisture reading needs a photo',
          path: ['moisture', 'readings', readingIndex, 'photo_media_id'],
        })
      }
    })
  })
export type CommitDeliveryPayload = z.infer<typeof commitDeliveryPayload>

export const VOID_DELIVERY_REASONS = [
  'WALK_AWAY',
  'DIVERTED_TO_DRY',
  'MOVED_TO_PAPER',
  // Deleted from Home's list of open farmers (several open at once on one phone).
  'WRONG_FARMER',
  'DUPLICATE',
] as const
export type VoidDeliveryReason = (typeof VOID_DELIVERY_REASONS)[number]

export const voidDeliveryPayload = z.object({
  delivery_ulid: deliveryUlid,
  session_id: id,
  farmer_id: id,
  /** Null when he left before the crop was picked. */
  crop_id: id.nullable(),
  crop_variety_id: id.nullable(),
  cultivation_id: id.nullable(),
  no_cultivation_reason: z.enum(['FIELD_NOT_ENROLLED']).nullable(),
  /** Lot codes minted for this draft, written VOID so they are never reused. */
  lots: z.array(z.object({ id, code: z.string().min(1), stream: z.enum(STREAMS) })).min(1),
  reason: z.enum(VOID_DELIVERY_REASONS),
  /** DIVERTED_TO_DRY: e.g. LOOKS_WET (roster divert_reasons). */
  reason_code: z.string().nullable(),
  /** Bags tagged before the cancel (each tag is in voided_tags). */
  bags: z.array(bagSchema),
  voided_tags: z.array(voidedTagSchema),
  voided_seal: z.object({ qr_payload: z.string().min(1), occurred_at: isoTs }).nullable(),
  refused_scans: z.array(refusedScanSchema),
  /** Readings taken before a divert are kept (F4-B1.a). */
  moisture: moistureSchema.nullable(),
  disputes: z.array(disputeSchema),
  voided_at: isoTs,
})
export type VoidDeliveryPayload = z.infer<typeof voidDeliveryPayload>

// ---------------------------------------------------------------------------------------------
// F4.6 void_tag / retag, F4-B3 spoil_slip, F4.11 close_session
// ---------------------------------------------------------------------------------------------

/** A tag or seal spoiled outside any delivery (torn on the roll). */
export const voidTagPayload = z.object({
  qr_payload: z.string().min(1),
  kind: z.enum(['BAG', 'SEAL']),
  void_reason: z.enum(['TORN', 'MISPRINT', 'OTHER']),
  occurred_at: isoTs,
})
export type VoidTagPayload = z.infer<typeof voidTagPayload>

/** Supervisor only: a new tag or seal for a bag/sample held for a tag conflict. */
export const retagPayload = z
  .object({
    target: z.enum(['BAG', 'SAMPLE']),
    container_id: id.nullable(),
    sample_id: id.nullable(),
    qr_payload: z.string().min(1),
    entry: z.enum(TAG_ENTRY),
  })
  .refine((r) => (r.target === 'BAG' ? r.container_id !== null : r.sample_id !== null), {
    message: 'container_id for BAG, sample_id for SAMPLE',
  })
export type RetagPayload = z.infer<typeof retagPayload>

export const spoilSlipPayload = z.object({
  receipt_slip: receiptSlipSchema,
  session_id: id,
  occurred_at: isoTs,
})
export type SpoilSlipPayload = z.infer<typeof spoilSlipPayload>

export const closeSessionPayload = z.object({
  closed_at: isoTs,
  /** V1: no evening hand count, both null. */
  tags_counted: z.number().int().nonnegative().nullable(),
  seals_counted: z.number().int().nonnegative().nullable(),
  /** Commands of this session made on this phone (excluding this one): the server reconciles once all arrived. */
  command_count: z.number().int().nonnegative(),
  lots_main: z.number().int().nonnegative(),
  lots_conv: z.number().int().nonnegative(),
  /** PAPER: the used slip range, checked for gaps (F4-B3.b). */
  slip_range: z
    .object({ book_id: id, first_slip_no: slipNo, last_slip_no: slipNo })
    .nullable()
    .default(null),
})
export type CloseSessionPayload = z.infer<typeof closeSessionPayload>

registerCommand('open_session', openSessionPayload)
registerCommand('confirm_roll', confirmRollPayload)
registerCommand('commit_delivery', commitDeliveryPayload)
registerCommand('void_delivery', voidDeliveryPayload)
registerCommand('void_tag', voidTagPayload)
registerCommand('retag', retagPayload)
registerCommand('spoil_slip', spoilSlipPayload)
registerCommand('close_session', closeSessionPayload)

// ---------------------------------------------------------------------------------------------
// V1 add_bank_account — typed in offline, verified through OnGrid when sync resumes
// ---------------------------------------------------------------------------------------------

/**
 * The full number rides in the sealed outbox only until the server answers (scrubSyncedBank
 * deletes the record); the server encrypts the verified number and masks the command record.
 */
export const addBankAccountPayload = z.object({
  farmer_id: id,
  account_number: z.string().regex(/^\d{6,18}$/, '6 to 18 digits'),
  ifsc: z.string().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'IFSC'),
  name_on_passbook: z.string().max(120).nullable(),
  consent: z.literal(true),
})
export type AddBankAccountPayload = z.infer<typeof addBankAccountPayload>
registerCommand('add_bank_account', addBankAccountPayload)

// ---------------------------------------------------------------------------------------------
// What the server sends back in results[].server_state (read leniently; all optional)
// ---------------------------------------------------------------------------------------------

export const commitServerState = z.looseObject({
  lots: z
    .array(
      z.looseObject({
        id: z.string(),
        code: z.string().nullish(),
        status: z.string().nullish(),
        hold: z.string().nullish(),
        hold_reason: z.string().nullish(),
        hold_reasons: z.array(z.string()).nullish(),
        /** F4.9-l: payout status and bank reference, never an amount. */
        payout_status: z.string().nullish(),
        utr: z.string().nullish(),
      }),
    )
    .default([]),
  /** Bags / samples kept without a tag — the supervisor's re-tag list (F4.6-h). */
  waiting_for_tag: z
    .array(
      z.looseObject({
        target: z.enum(['BAG', 'SAMPLE']),
        container_id: z.string().nullish(),
        sample_id: z.string().nullish(),
        conflict_qr_payload: z.string().nullish(),
        lot_code: z.string().nullish(),
      }),
    )
    .default([]),
})
export type CommitServerState = z.infer<typeof commitServerState>

export const closeServerState = z.looseObject({
  balanced: z.boolean().nullish(),
  exception_id: z.string().nullish(),
})
export type CloseServerState = z.infer<typeof closeServerState>
